import { useEffect, useState } from 'react'
import {
  ArrowLeft, Camera, ChevronDown, ChevronUp, ClipboardList, Plus, Trash2, TriangleAlert,
} from 'lucide-react'
import { Bidi, Card, CardHeader, ErrorState, LoadingState, useLocale } from '@hk/ui'
import {
  discardChecklistDraft, formatCount, openChecklistDraft, publishChecklistDraft,
  renameChecklistTemplate, saveChecklistDraft, translateError,
  type ChecklistDraftView, type ChecklistItemDraft, type ChecklistTemplateRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-126 · TEMPLATE EDITOR — a draft version, with the change from the live one stated
   plainly.

   **THE PANEL SAYING WHAT CHANGES IS THE POINT OF THE SCREEN**, and pack 5 says why. Its
   worked example is a draft that makes a bathroom photograph required: worth ten seconds a
   room, because that is the commonest inspection failure — and also about two hundred and
   forty photographs a day across both properties, which is a storage and upload bill
   somebody else pays. **The trade goes in front of the person making the change rather
   than being discovered by the person paying for it.**

   THE DIFF AND THE PHOTOGRAPH COUNT COME FROM THE SERVER. They are rules about what a
   change costs the hotel; computing them here would be a second implementation and, on
   this screen, a second number — one of which somebody would act on.

   NOTHING HERE CAN TOUCH THE LIVE VERSION. There is no call in `@hk/core` that could: a
   draft is saved, and publishing stamps the draft. The live version is left exactly where
   it was, which is what lets the screen promise that rooms cleaned under v7 keep their v7
   record.

   REORDERING IS TWO BUTTONS, NOT DRAG-AND-DROP. The pack draws dragging, and dragging is
   better with a mouse — but this list is edited rarely, by one person, and a keyboard user
   or anybody on a touchpad gets nothing from a drag handle. Buttons work for everyone and
   the order is what an attendant sees on the handset, so getting it right matters more
   than getting it elegant. Worth revisiting if the client asks.
   ========================================================================= */

export function ChecklistEditor({
  propertyId, template, onDone,
}: {
  propertyId: string
  template: ChecklistTemplateRow
  onDone: () => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [draft, setDraft] = useState<ChecklistDraftView | null>(null)
  const [items, setItems] = useState<ChecklistItemDraft[]>([])
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState<'saving' | 'publishing' | null>(null)
  const [said, setSaid] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setError(null)
    openChecklistDraft(propertyId, template.templateId)
      .then((next) => { if (live) { setDraft(next); setItems(next.items) } })
      .catch((caught: unknown) => { if (live) setError(caught) })
    return () => { live = false }
  }, [propertyId, template.templateId])

  async function save() {
    setBusy('saving'); setError(null)
    try {
      const next = await saveChecklistDraft(propertyId, template.templateId, items)
      setDraft(next); setItems(next.items); setSaid(t.clSaved)
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  async function publish() {
    setBusy('publishing'); setError(null)
    try {
      /* SAVED FIRST, ALWAYS. Publishing what is on the server rather than what is on the
         screen would publish the last SAVED draft while the person is looking at unsaved
         edits — and they would have no way of telling. One button, both steps. */
      const saved = await saveChecklistDraft(propertyId, template.templateId, items)
      const published = await publishChecklistDraft(propertyId, template.templateId)
      setDraft(published)
      setSaid(t.clPublished.replace('{version}', String(published.draftVersionNumber)))
      void saved
      onDone()
    } catch (caught: unknown) {
      setError(caught)
    } finally {
      setBusy(null)
    }
  }

  async function discard() {
    setError(null)
    try {
      await discardChecklistDraft(propertyId, template.templateId)
      onDone()
    } catch (caught: unknown) {
      setError(caught)
    }
  }

  function move(index: number, by: -1 | 1) {
    const next = [...items]
    const to = index + by
    if (to < 0 || to >= next.length) return
    ;[next[index], next[to]] = [next[to]!, next[index]!]
    setItems(next)
  }

  if (error !== null && draft === null) {
    return <ErrorState body={translateError(error, locale).message} onRetry={onDone} />
  }
  if (!draft) return <LoadingState />

  const live = draft.liveVersionNumber

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader
          title={`${nameOf(draft, locale)} · v${draft.draftVersionNumber}`}
          icon={<ClipboardList className="size-4" />}
        />

        {/* ── WHAT THIS LIST IS CALLED, IN BOTH LANGUAGES ────────────────────────

            A NAME IS NOT VERSIONED. Editing it saves immediately and makes no draft:
            calling the same list "Departure clean" or "Departure clean — tower A"
            changes nothing an attendant does and nothing an inspection is read against,
            and versioning it would hand every handset in the hotel a "new" checklist
            for a change nobody made. It sits ABOVE the version controls for that reason.

            AND THE ARABIC BOX EXISTS AT ALL BECAUSE OF W-127. Templates were named in
            English only from Sprint 23; nobody noticed, because the two screens that
            showed a template name showed one the administrator had typed themselves.
            W-127's cleaning-rules half is the first to put it beside Arabic text — and
            there it read as Latin script down the middle of an Arabic table. */}
        <TemplateNames
          propertyId={propertyId}
          draft={draft}
          locale={locale}
          t={t}
          onRenamed={(row) => { setDraft((d) => (d ? { ...d, name: row.name, nameAr: row.nameAr } : d)) }}
        />

        <div className="flex flex-wrap items-center gap-2.5 p-3.5 pt-0">
          <button
            type="button"
            onClick={onDone}
            className="inline-flex items-center gap-1.5 rounded-hk border border-border
                       px-3 py-1.5 text-xs font-medium hover:bg-muted cursor-pointer"
          >
            <ArrowLeft className="size-3.5 rtl:rotate-180" />
            {t.clBack}
          </button>
          <span className="text-[12.5px] text-muted-foreground">
            {live === null
              ? t.clDraftFirst
              : t.clDraftOf.replace('{live}', String(live))}
          </span>
        </div>
      </Card>

      {said !== null && (
        <div role="status" className="rounded-hk border border-[#86efac] bg-[#f0fdf4] px-3.5 py-3
                                      text-[12.5px] text-[#166534] dark:border-[#166534]
                                      dark:bg-[#0d2417] dark:text-[#86efac]">
          <Bidi>{said}</Bidi>
        </div>
      )}

      {error !== null && (
        <div role="alert" className="flex items-start gap-2 rounded-hk border border-[#fca5a5]
                                     bg-[#fef2f2] px-3.5 py-3 text-[12.5px] text-[#991b1b]
                                     dark:border-[#7f1d1d] dark:bg-[#2a0f0f] dark:text-[#fca5a5]">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <span><Bidi>{translateError(error, locale).message}</Bidi></span>
        </div>
      )}

      <div className="grid gap-3.5 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader title={t.clItemsTitle} icon={<ClipboardList className="size-4" />} />
          <div className="flex flex-col gap-2 p-3.5">
            <p className="text-[11.5px] text-muted-foreground">{t.clItemsHint}</p>

            {items.map((item, index) => (
              <div
                key={`${item.code}-${index}`}
                className="flex flex-wrap items-start gap-2 rounded-hk border border-border p-2.5"
              >
                <span className="w-6 shrink-0 pt-2 text-center font-mono text-[12px] text-muted-foreground">
                  {index + 1}
                </span>

                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <div className="flex flex-wrap gap-1.5">
                    <input
                      value={item.code}
                      onChange={(e) => setItems(replace(items, index, { code: e.target.value }))}
                      aria-label={`${t.clItemCode} ${index + 1}`}
                      placeholder={t.clItemCode}
                      className="h-8 w-44 rounded-hk border border-input bg-background px-2
                                 font-mono text-[12px]"
                    />
                    <input
                      value={item.labelEn}
                      onChange={(e) => setItems(replace(items, index, { labelEn: e.target.value }))}
                      aria-label={`${t.clItemEn} ${index + 1}`}
                      placeholder={t.clItemEn}
                      dir="ltr"
                      className="h-8 min-w-40 flex-1 rounded-hk border border-input bg-background
                                 px-2 text-[12.5px]"
                    />
                    {/* THE ARABIC BOX IS ALWAYS RIGHT-TO-LEFT, whatever language the person
                        editing is reading the screen in. An English-speaking administrator
                        typing Arabic into a left-aligned box cannot tell where the words
                        have gone. */}
                    <input
                      value={item.labelAr}
                      onChange={(e) => setItems(replace(items, index, { labelAr: e.target.value }))}
                      aria-label={`${t.clItemAr} ${index + 1}`}
                      placeholder={t.clItemAr}
                      dir="rtl"
                      className="h-8 min-w-40 flex-1 rounded-hk border border-input bg-background
                                 px-2 text-[12.5px]"
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-[11.5px]">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={item.isMandatory}
                        onChange={(e) =>
                          setItems(replace(items, index, { isMandatory: e.target.checked }))}
                        className="size-3.5 cursor-pointer"
                      />
                      {item.isMandatory ? t.clRequired : t.clOptional}
                    </label>
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={item.requiresPhoto}
                        onChange={(e) =>
                          setItems(replace(items, index, { requiresPhoto: e.target.checked }))}
                        className="size-3.5 cursor-pointer"
                      />
                      <Camera className="size-3" />
                      {t.clPhoto}
                    </label>
                  </div>
                </div>

                <div className="flex shrink-0 gap-1">
                  <IconButton label={t.clUp} onClick={() => move(index, -1)} disabled={index === 0}>
                    <ChevronUp className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label={t.clDown}
                    onClick={() => move(index, 1)}
                    disabled={index === items.length - 1}
                  >
                    <ChevronDown className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label={`${t.clRemove} ${index + 1}`}
                    onClick={() => setItems(items.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="size-3.5" />
                  </IconButton>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => setItems([
                ...items,
                { code: '', labelEn: '', labelAr: '', isMandatory: true, requiresPhoto: false },
              ])}
              className="inline-flex w-fit items-center gap-1.5 rounded-hk border border-border
                         px-3 py-1.5 text-xs font-medium hover:bg-muted cursor-pointer"
            >
              <Plus className="size-3.5" />
              {t.clAddItem}
            </button>
          </div>
        </Card>

        {/* ── what changes ────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3.5">
          <Card>
            <CardHeader
              title={live === null
                ? t.clChangesTitle.replace('v{live}', '').trim()
                : t.clChangesTitle.replace('{live}', String(live))}
              icon={<TriangleAlert className="size-4 text-[#b45309]" />}
            />
            <div className="flex flex-col gap-2 p-3.5">
              {draft.changes.length === 0
                ? <p className="text-[12.5px] text-muted-foreground">{t.clChangesNone}</p>
                : (
                  <ul className="flex flex-col gap-1.5">
                    {draft.changes.map((change, index) => (
                      <li key={`${change.code}-${change.change}-${index}`}
                        className="text-[12.5px] leading-snug">
                        <Bidi>
                          {(t.clChanges[change.change] ?? '{code}')
                            .replace('{code}', change.code)}
                        </Bidi>
                      </li>
                    ))}
                  </ul>
                )}

              {/* THE NUMBER WITH A BILL ATTACHED. Only drawn when the photographs change:
                  a line saying "0 more photographs a day" on every save would train the
                  reader to skip the one time it says 240. */}
              {draft.extraPhotographsPerDay !== null && draft.extraPhotographsPerDay !== 0 && (
                <p className={`rounded-hk border px-2.5 py-2 text-[12px] leading-snug
                               ${draft.extraPhotographsPerDay > 0
                                 ? 'border-[#fcd34d] bg-[#fffbeb] text-[#92400e] dark:border-[#92400e] dark:bg-[#2a1f07] dark:text-[#fcd34d]'
                                 : 'border-border text-muted-foreground'}`}>
                  <Bidi>
                    {(draft.extraPhotographsPerDay > 0 ? t.clPhotoCost : t.clPhotoSaving)
                      .replace('{count}',
                        formatCount(Math.abs(draft.extraPhotographsPerDay), locale))
                      .replace('{cleans}', formatCount(draft.cleansPerDay, locale))}
                  </Bidi>
                </p>
              )}
              {draft.extraPhotographsPerDay === null
                && items.some((i) => i.requiresPhoto)
                && (
                  <p className="text-[11.5px] leading-snug text-muted-foreground">
                    {t.clPhotoUnknown}
                  </p>
                )}

              {live !== null && (
                <p className="border-t border-border pt-2 text-[11.5px] leading-snug text-muted-foreground">
                  {t.clNotReEvaluated.replace('{live}', String(live))}
                </p>
              )}
            </div>
          </Card>

          <Card>
            <div className="flex flex-col gap-2 p-3.5">
              <button
                type="button"
                onClick={() => void publish()}
                disabled={busy !== null || items.length === 0}
                className="rounded-hk bg-[var(--hk-primary)] px-3.5 py-2 text-xs font-medium
                           text-white disabled:bg-muted disabled:text-muted-foreground
                           disabled:cursor-not-allowed cursor-pointer"
              >
                {busy === 'publishing'
                  ? t.clPublishing
                  : t.clPublish.replace('{version}', String(draft.draftVersionNumber))}
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={busy !== null}
                className="rounded-hk border border-border px-3.5 py-2 text-xs font-medium
                           hover:bg-muted disabled:opacity-60 cursor-pointer"
              >
                {busy === 'saving' ? t.clSaving : t.clSave}
              </button>
              <button
                type="button"
                onClick={() => void discard()}
                disabled={busy !== null}
                className="rounded-hk px-3.5 py-2 text-xs font-medium text-muted-foreground
                           hover:bg-muted cursor-pointer"
              >
                {t.clDiscard}
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

function replace(
  items: ChecklistItemDraft[], index: number, patch: Partial<ChecklistItemDraft>,
): ChecklistItemDraft[] {
  return items.map((item, i) => (i === index ? { ...item, ...patch } : item))
}

function IconButton({
  label, onClick, disabled, children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled ?? false}
      aria-label={label}
      className="rounded-hk border border-border p-1.5 hover:bg-muted
                 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
    >
      {children}
    </button>
  )
}

/** The name in the reader's language, falling back to the English rather than a blank. */
function nameOf(draft: ChecklistDraftView, locale: 'en' | 'ar') {
  return locale === 'ar' ? draft.nameAr ?? draft.name : draft.name
}

/**
 * WHAT THIS LIST IS CALLED, IN BOTH LANGUAGES.
 *
 * **SAVED IMMEDIATELY, AND IT MAKES NO VERSION.** A version exists because what a clean
 * CONSISTS OF changed, so a room cleaned in July can still be read against July's rules.
 * A name is not part of that. Versioning a rename would fill the history with entries
 * that say nothing and — worse — hand every handset in the hotel a "new" checklist for a
 * change nobody made.
 *
 * That is also why this sits above the draft controls rather than inside them: an
 * administrator editing the name is not editing the clean, and a Save button shared with
 * the item list would say otherwise.
 */
function TemplateNames({
  propertyId, draft, locale, t, onRenamed,
}: {
  propertyId: string
  draft: ChecklistDraftView
  locale: 'en' | 'ar'
  t: (typeof STRINGS)['en']
  onRenamed: (row: { name: string; nameAr: string | null }) => void
}) {
  const [name, setName] = useState(draft.name)
  const [nameAr, setNameAr] = useState(draft.nameAr ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)

  const dirty = name.trim() !== draft.name || nameAr.trim() !== (draft.nameAr ?? '')

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const row = await renameChecklistTemplate(propertyId, draft.templateId, {
        name: name.trim(),
        // Sent even when empty — an empty Arabic name CLEARS it back to the English
        // fallback, which is a real intention and the only way to undo a mistyping.
        nameAr: nameAr.trim(),
      })
      onRenamed(row)
      setSaved(true)
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-2 border-b border-border p-3.5">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-[12.5px]">
          <span className="font-medium">{t.clNameEn}</span>
          <input
            value={name}
            onChange={(e) => { setName(e.target.value); setSaved(false) }}
            /* `dir="auto"` on BOTH boxes. A name typed in Arabic into the English field
               must not render backwards, and the reverse is just as true — the rule is
               about the CONTENT's direction, not the field's label. */
            dir="auto"
            aria-label={t.clNameEn}
            className="h-8 rounded-hk border border-input bg-background px-2.5 text-[12.5px]"
          />
        </label>
        <label className="flex flex-col gap-1 text-[12.5px]">
          <span className="font-medium">{t.clNameAr}</span>
          <input
            value={nameAr}
            onChange={(e) => { setNameAr(e.target.value); setSaved(false) }}
            dir="auto"
            placeholder={draft.name}
            aria-label={t.clNameAr}
            className="h-8 rounded-hk border border-input bg-background px-2.5 text-[12.5px]"
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy || !dirty || name.trim().length === 0}
          className="rounded-hk border border-border px-3 py-1.5 text-xs font-medium
                     hover:bg-muted cursor-pointer disabled:cursor-not-allowed
                     disabled:opacity-50"
        >
          {busy ? t.editSaving : t.clSaveName}
        </button>
        {/* SAID OUT LOUD THAT IT DOES NOT MAKE A VERSION. An administrator who has just
            learned that every other change here creates one will assume this did too,
            and will go looking for a draft to publish. */}
        <span className="text-[11px] text-muted-foreground">{t.clNameNoVersion}</span>
        {saved && !dirty && (
          <span role="status" className="text-[11px] text-[#166534] dark:text-[#86efac]">
            {t.clNameSaved}
          </span>
        )}
        {error !== null && (
          <span className="text-[11px] text-[var(--hk-danger)]">
            <Bidi>{translateError(error, locale).message}</Bidi>
          </span>
        )}
      </div>
    </div>
  )
}
